import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Upload, Image as ImageIcon, Save, Building2, ScrollText, Plus, Trash2, Sliders, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import ResetDataCard from './ResetDataCard';
import BackupRestoreCard from './BackupRestoreCard';
import EcgSoundAdminCard from './EcgSoundAdminCard';
import DedupeCard from './DedupeCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fileToDataURL = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const DEFAULT_AGREEMENT_PARAS = [
  "The Customer hereby acknowledges having received the above-mentioned product from MANHA DIGITAL in good working condition and agrees to pay the monthly installment amount on or before the due date of each month.",
  "The product remains the exclusive property of MANHA DIGITAL until the full payment has been received in full. In case of default of two or more consecutive installments, the Company reserves the right to repossess the product without prior notice and, if applicable, invoke the mobile lock service.",
  "The Customer confirms that all information and documents provided (CNIC, photograph, signature, thumb impression) are true and correct and authorises MANHA DIGITAL to verify the same by any lawful means.",
  "Any dispute arising out of this Agreement shall be settled exclusively under the jurisdiction of the courts of Pakistan.",
];

export default function CompanySettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    company_name: 'MANHA DIGITAL', subtitle: 'Your Dreams, Our Deals',
    address: '', phone: '', email: '', ntn: '',
    logo_url: '', login_bg_url: '',
    agreement_paragraphs: [], guarantor_paragraph: '', surety_cheque_text: '',
    employment_verification_text: '', customer_consent_text: '', noc_text: '', legal_notice_text: '',
    document_settings: {
      logo_size: 'medium', logo_position: 'left',
      primary_color: '#020617', accent_color: '#D97706',
      font_family: 'Helvetica', font_size: 10,
      background_url: '',
    },
  });
  const [saving, setSaving] = useState(false);
  const [paymentQrs, setPaymentQrs] = useState({});
  const [qrBusy, setQrBusy] = useState('');
  const logoRef = useRef(null);
  const bgRef = useRef(null);
  const docBgRef = useRef(null);

  const updateDoc = (patch) => setSettings((s) => ({ ...s, document_settings: { ...(s.document_settings || {}), ...patch } }));

  useEffect(() => { fetchSettings(); fetchPaymentQrs(); }, []);

  const fetchPaymentQrs = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/payment-qr`, { withCredentials: true });
      setPaymentQrs(data || {});
    } catch { /* ignore */ }
  };

  const handleQrUpload = async (method, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('QR image must be under 2MB'); return; }
    const dataUrl = await fileToDataURL(file);
    setQrBusy(method);
    try {
      const { data } = await axios.put(`${API_URL}/api/settings/payment-qr`,
        { method, image: dataUrl }, { withCredentials: true });
      setPaymentQrs(data.payment_qr_codes || {});
      toast.success(`${method} QR saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setQrBusy(''); }
  };

  const handleQrRemove = async (method) => {
    setQrBusy(method);
    try {
      const { data } = await axios.put(`${API_URL}/api/settings/payment-qr`,
        { method, image: null }, { withCredentials: true });
      setPaymentQrs(data.payment_qr_codes || {});
      toast.success(`${method} QR removed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    } finally { setQrBusy(''); }
  };

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/company`, { withCredentials: true });
      setSettings((prev) => ({
        ...prev, ...data,
        agreement_paragraphs: data.agreement_paragraphs?.length ? data.agreement_paragraphs : DEFAULT_AGREEMENT_PARAS,
        document_settings: { ...prev.document_settings, ...(data.document_settings || {}) },
      }));
    } catch { toast.error('Failed to load settings'); }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('File must be under 2MB'); return; }
    const dataUrl = await fileToDataURL(file);
    setSettings({ ...settings, [field]: dataUrl });
    toast.success(`${field === 'logo_url' ? 'Logo' : 'Background'} loaded — click Save`);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/api/settings/company`, settings, { withCredentials: true });
      toast.success('Company settings saved.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const updatePara = (i, val) => {
    const arr = [...(settings.agreement_paragraphs || [])];
    arr[i] = val;
    setSettings({ ...settings, agreement_paragraphs: arr });
  };
  const addPara = () => setSettings({ ...settings, agreement_paragraphs: [...(settings.agreement_paragraphs || []), ''] });
  const removePara = (i) => {
    const arr = [...(settings.agreement_paragraphs || [])];
    arr.splice(i, 1);
    setSettings({ ...settings, agreement_paragraphs: arr });
  };

  return (
    <div className="space-y-6" data-testid="company-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="settings-title">Company Settings</h1>
          <p className="text-slate-400">Brand identity, legal templates & document text — all in one place</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-settings">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Tabs defaultValue="brand" className="space-y-6">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="brand" data-testid="tab-brand" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Brand</TabsTrigger>
          <TabsTrigger value="visuals" data-testid="tab-visuals" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Visuals</TabsTrigger>
          <TabsTrigger value="payment-qr" data-testid="tab-payment-qr" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Payment QR</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Documents</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Legal Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="brand">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Building2 className="h-5 w-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-white">Brand Information</h2>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Company Name</Label>
                  <Input value={settings.company_name || ''} data-testid="company-name-input"
                    onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Subtitle</Label>
                  <Input value={settings.subtitle || ''} data-testid="company-subtitle-input"
                    onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Phone</Label>
                  <Input value={settings.phone || ''} onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" value={settings.email || ''} onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">NTN</Label>
                  <Input value={settings.ntn || ''} onChange={(e) => setSettings({ ...settings, ntn: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Address</Label>
                  <Textarea value={settings.address || ''} onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" rows={2} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="visuals">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <ImageIcon className="h-5 w-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-white">Visual Assets</h2>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="text-slate-300">Company Logo</Label>
                <div className="border border-dashed border-slate-700 rounded-sm p-6 text-center bg-slate-800/40">
                  {settings.logo_url ? <img src={settings.logo_url} alt="Logo" className="mx-auto h-24 object-contain mb-3" />
                    : <div className="h-24 flex items-center justify-center text-slate-500 mb-3">No logo uploaded</div>}
                  <input ref={logoRef} type="file" accept="image/*" className="hidden"
                    data-testid="logo-file-input" onChange={(e) => handleImageUpload(e, 'logo_url')} />
                  <Button variant="ghost" onClick={() => logoRef.current?.click()}
                    className="text-amber-500 hover:text-amber-400 hover:bg-slate-800" data-testid="upload-logo-button">
                    <Upload className="h-4 w-4 mr-2" /> Upload Logo
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                <Label className="text-slate-300">Login Background Image</Label>
                <div className="border border-dashed border-slate-700 rounded-sm p-6 text-center bg-slate-800/40">
                  {settings.login_bg_url ? <img src={settings.login_bg_url} alt="Login BG" className="mx-auto h-32 w-full object-cover mb-3 rounded-sm" />
                    : <div className="h-32 flex items-center justify-center text-slate-500 mb-3">No background uploaded</div>}
                  <input ref={bgRef} type="file" accept="image/*" className="hidden"
                    data-testid="bg-file-input" onChange={(e) => handleImageUpload(e, 'login_bg_url')} />
                  <Button variant="ghost" onClick={() => bgRef.current?.click()}
                    className="text-amber-500 hover:text-amber-400 hover:bg-slate-800" data-testid="upload-bg-button">
                    <Upload className="h-4 w-4 mr-2" /> Upload Background
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payment-qr">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <ImageIcon className="h-5 w-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-white">Payment QR Codes</h2>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Upload one QR per payment method. During an unlock request the officer will see the matching QR so the customer can scan, pay, and share the Transaction ID back.
              </p>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['JazzCash', 'Easypaisa', 'Bank Transfer', 'Paid in Branch', 'Other'].map((method) => (
                <div key={method} className="border border-slate-800 rounded-sm p-3 bg-slate-950/40"
                  data-testid={`qr-card-${method.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-white text-sm">{method}</div>
                    {paymentQrs[method] && (
                      <Button variant="ghost" size="sm" onClick={() => handleQrRemove(method)}
                        disabled={qrBusy === method}
                        data-testid={`qr-remove-${method.toLowerCase().replace(/\s+/g, '-')}`}
                        className="h-7 text-red-400 hover:text-red-300 hover:bg-slate-800 px-2">
                        <Trash2 className="h-3 w-3 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {paymentQrs[method] ? (
                      <img src={paymentQrs[method]} alt={`${method} QR`}
                        className="h-28 w-28 bg-white p-2 rounded object-contain" />
                    ) : (
                      <div className="h-28 w-28 border border-dashed border-slate-700 rounded flex items-center justify-center text-slate-500 text-xs">
                        No QR uploaded
                      </div>
                    )}
                    <label className="flex-1">
                      <input type="file" accept="image/*" className="hidden"
                        data-testid={`qr-upload-${method.toLowerCase().replace(/\s+/g, '-')}`}
                        onChange={(e) => handleQrUpload(method, e.target.files?.[0])} />
                      <Button asChild variant="outline"
                        className="w-full bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                        disabled={qrBusy === method}>
                        <span>
                          <Upload className="h-4 w-4 mr-2" />
                          {paymentQrs[method] ? 'Replace' : 'Upload'} QR
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Sliders className="h-5 w-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-white">Document Customization</h2>
              </div>
              <p className="text-xs text-slate-500 mt-1">Applies to every generated PDF — agreements, receipts, schedules, investor slips and statements.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Logo Size</Label>
                  <select value={settings.document_settings?.logo_size || 'medium'}
                    data-testid="doc-logo-size"
                    onChange={(e) => updateDoc({ logo_size: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-3 text-sm">
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Logo Position</Label>
                  <select value={settings.document_settings?.logo_position || 'left'}
                    data-testid="doc-logo-position"
                    onChange={(e) => updateDoc({ logo_position: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-3 text-sm">
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Font Family</Label>
                  <select value={settings.document_settings?.font_family || 'Helvetica'}
                    data-testid="doc-font-family"
                    onChange={(e) => updateDoc({ font_family: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-3 text-sm">
                    <option value="Helvetica">Helvetica (Sans)</option>
                    <option value="Times-Roman">Times Roman (Serif)</option>
                    <option value="Courier">Courier (Mono)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Body Font Size (pt)</Label>
                  <Input type="number" min="8" max="16"
                    data-testid="doc-font-size"
                    value={settings.document_settings?.font_size ?? 10}
                    onChange={(e) => updateDoc({ font_size: Number(e.target.value) || 10 })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Primary Color (headings)</Label>
                  <div className="flex items-center space-x-2">
                    <Input type="color" value={settings.document_settings?.primary_color || '#020617'}
                      data-testid="doc-primary-color"
                      onChange={(e) => updateDoc({ primary_color: e.target.value })}
                      className="bg-slate-800 border-slate-700 h-9 w-16 p-1" />
                    <Input value={settings.document_settings?.primary_color || '#020617'}
                      onChange={(e) => updateDoc({ primary_color: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white flex-1" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Accent Color (subtitle)</Label>
                  <div className="flex items-center space-x-2">
                    <Input type="color" value={settings.document_settings?.accent_color || '#D97706'}
                      data-testid="doc-accent-color"
                      onChange={(e) => updateDoc({ accent_color: e.target.value })}
                      className="bg-slate-800 border-slate-700 h-9 w-16 p-1" />
                    <Input value={settings.document_settings?.accent_color || '#D97706'}
                      onChange={(e) => updateDoc({ accent_color: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white flex-1" />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-slate-300">Document Background Image</Label>
                <p className="text-xs text-slate-500">Rendered as a soft watermark behind every PDF page. PNG / JPG · &lt; 2 MB.</p>
                <div className="border border-dashed border-slate-700 rounded-sm p-6 text-center bg-slate-800/40 relative">
                  {settings.document_settings?.background_url ? (
                    <>
                      <img src={settings.document_settings.background_url} alt="Doc BG"
                        className="mx-auto h-40 w-full object-cover mb-3 rounded-sm opacity-70" />
                      <button type="button" onClick={() => updateDoc({ background_url: '' })}
                        data-testid="doc-bg-delete"
                        className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-sm text-white">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : (
                    <div className="h-40 flex items-center justify-center text-slate-500 mb-3">No background uploaded</div>
                  )}
                  <input ref={docBgRef} type="file" accept="image/*" className="hidden"
                    data-testid="doc-bg-file-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      if (file.size > 2 * 1024 * 1024) { toast.error('Background must be under 2MB'); return; }
                      const url = await fileToDataURL(file);
                      updateDoc({ background_url: url });
                      toast.success('Background loaded — click Save');
                    }} />
                  <div className="flex justify-center space-x-2">
                    <Button variant="ghost" onClick={() => docBgRef.current?.click()}
                      data-testid="doc-bg-upload"
                      className="text-amber-500 hover:text-amber-400 hover:bg-slate-800">
                      <Upload className="h-4 w-4 mr-2" /> {settings.document_settings?.background_url ? 'Replace Background' : 'Upload Background'}
                    </Button>
                    {settings.document_settings?.background_url && (
                      <Button variant="ghost" onClick={() => updateDoc({ background_url: '' })}
                        data-testid="doc-bg-delete-btn"
                        className="text-red-400 hover:text-red-300 hover:bg-slate-800">
                        <Trash2 className="h-4 w-4 mr-2" /> Delete Background
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Live preview */}
              <div className="border border-slate-700 rounded-sm p-4 bg-white text-slate-900 relative overflow-hidden"
                data-testid="doc-preview"
                style={{ fontFamily: settings.document_settings?.font_family === 'Times-Roman' ? 'Times New Roman, serif' : settings.document_settings?.font_family === 'Courier' ? 'Courier New, monospace' : 'Helvetica, Arial, sans-serif' }}>
                {settings.document_settings?.background_url && (
                  <img src={settings.document_settings.background_url} alt="bg"
                    className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none" />
                )}
                <div className={`relative flex items-start gap-4 ${settings.document_settings?.logo_position === 'center' ? 'flex-col items-center' : settings.document_settings?.logo_position === 'right' ? 'flex-row-reverse' : ''}`}>
                  {settings.logo_url && (
                    <img src={settings.logo_url} alt="Logo"
                      className="object-contain"
                      style={{ width: settings.document_settings?.logo_size === 'large' ? 100 : settings.document_settings?.logo_size === 'small' ? 56 : 76, height: settings.document_settings?.logo_size === 'large' ? 100 : settings.document_settings?.logo_size === 'small' ? 56 : 76 }} />
                  )}
                  <div className={settings.document_settings?.logo_position === 'center' ? 'text-center' : 'flex-1'}>
                    <div className="font-bold" style={{ color: settings.document_settings?.primary_color, fontSize: (settings.document_settings?.font_size || 10) + 10 }}>{settings.company_name}</div>
                    <div className="italic" style={{ color: settings.document_settings?.accent_color, fontSize: settings.document_settings?.font_size || 10 }}>{settings.subtitle}</div>
                  </div>
                </div>
                <div className="relative mt-3 py-1 px-2 text-white text-sm font-semibold" style={{ backgroundColor: settings.document_settings?.primary_color }}>
                  SAMPLE SECTION HEADER
                </div>
                <p className="relative mt-2 text-xs" style={{ fontSize: settings.document_settings?.font_size || 10, color: settings.document_settings?.primary_color }}>
                  Live preview — this is how your document headers and body copy will look. Save to apply.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <ScrollText className="h-5 w-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-white">Editable Legal Templates</h2>
              </div>
              <p className="text-xs text-slate-500 mt-1">Every paragraph below is injected into the corresponding PDF at print/download time.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300 text-base">Agreement Terms &amp; Conditions</Label>
                  <Button variant="outline" size="sm" onClick={addPara}
                    data-testid="add-agreement-paragraph"
                    className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white">
                    <Plus className="h-4 w-4 mr-1" /> Add Paragraph
                  </Button>
                </div>
                {(settings.agreement_paragraphs || []).map((p, i) => (
                  <div key={i} className="flex items-start space-x-2" data-testid={`agreement-paragraph-${i}`}>
                    <span className="text-slate-500 text-sm mt-3 w-6">{i + 1}.</span>
                    <Textarea value={p} onChange={(e) => updatePara(i, e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white flex-1" rows={2} />
                    <Button variant="ghost" size="icon" onClick={() => removePara(i)}
                      data-testid={`remove-paragraph-${i}`}
                      className="text-red-400 hover:text-red-300">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {[
                ['guarantor_paragraph', 'Guarantor Consent Paragraph'],
                ['surety_cheque_text', 'Surety Cheque Consent'],
                ['employment_verification_text', 'Employment Verification Certificate Body'],
                ['customer_consent_text', 'Customer Consent Declaration'],
                ['noc_text', 'No Objection Certificate (NOC) Body'],
                ['legal_notice_text', 'Legal Notice Body'],
              ].map(([key, label]) => (
                <div key={key} className="space-y-2">
                  <Label className="text-slate-300">{label}</Label>
                  <Textarea value={settings[key] || ''}
                    onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                    data-testid={`template-${key}`}
                    className="bg-slate-800 border-slate-700 text-white" rows={4} />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {user?.role === 'Super Admin' && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-white flex items-center pt-4" data-testid="database-restore-section">
            <span className="text-cyan-400 mr-2">Database Restore</span>
            <span className="text-xs text-slate-500 font-normal">— two independent workflows</span>
          </h2>
          <BackupRestoreCard />
          <EcgSoundAdminCard />
          <DedupeCard />
        </div>
      )}
      {user?.role === 'Super Admin' && <ResetDataCard />}
    </div>
  );
}
